// Lecture de la carte pivot : index des objets et des liens, zone, criticité, vue de l'ANSSI, filtres et
// disposition du graphe en couches (métier, applications, infrastructure, réseau, écosystème).
import type { Carte, Contradiction, Lien, Objet, TypeObjet, VueAnssi } from '../api/types';
import { idCourt } from './format';

export type Couche = 'metier' | 'applicatif' | 'technique' | 'reseau' | 'ecosysteme';

export const COUCHE_DU_TYPE: Record<TypeObjet, Couche> = {
  processus: 'metier',
  acteur: 'ecosysteme',
  donnee: 'applicatif',
  application: 'applicatif',
  agent_ia: 'applicatif',
  interface: 'applicatif',
  machine: 'technique',
  sauvegarde: 'technique',
  compte_privilegie: 'technique',
  protection: 'technique',
  zone_reseau: 'reseau',
  site: 'ecosysteme',
  fournisseur: 'ecosysteme',
  contrat: 'ecosysteme',
  constat: 'ecosysteme',
  exigence: 'ecosysteme',
  contrainte: 'ecosysteme',
  travaux: 'ecosysteme',
  decision: 'ecosysteme',
  signal: 'ecosysteme',
};

export const ORDRE_COUCHES: { id: Couche; libelle: string }[] = [
  { id: 'metier', libelle: 'Métier' },
  { id: 'applicatif', libelle: 'Applications' },
  { id: 'technique', libelle: 'Infrastructure' },
  { id: 'reseau', libelle: 'Réseau' },
  { id: 'ecosysteme', libelle: 'Écosystème' },
];

/** Vue du guide de l'ANSSI portée par l'objet (alignement « ANSSI, guide de cartographie du SI »). */
export function vueAnssiDe(o: Objet): VueAnssi | null {
  const a = o.alignements?.standards?.find((s) => s.standard.startsWith('ANSSI'));
  const m = a?.classe.match(/^vue (\w+)$/);
  if (m?.[1]) return m[1] as VueAnssi;
  // Repli quand l'alignement manque (objets créés par le DSI) : la vue la plus proche du type.
  const parType: Partial<Record<TypeObjet, VueAnssi>> = {
    processus: 'metier',
    acteur: 'metier',
    application: 'applications',
    interface: 'applications',
    donnee: 'applications',
    machine: 'infrastructures_physiques',
    sauvegarde: 'infrastructures_physiques',
    site: 'infrastructures_physiques',
    zone_reseau: 'infrastructures_logiques',
    compte_privilegie: 'administration',
    fournisseur: 'ecosysteme',
    contrat: 'ecosysteme',
  };
  return parType[o.type] ?? null;
}

export interface IndexCarte {
  carte: Carte;
  objets: Objet[];
  liens: Lien[];
  parId: Map<string, Objet>;
  sortants: Map<string, Lien[]>;
  entrants: Map<string, Lien[]>;
  /** Zone réseau de chaque objet (lien situe_dans vers une zone). */
  zone: Map<string, string>;
  /** Criticité 1–4 : celle du processus, ou la plus haute des processus qui en ont besoin (directement ou non). */
  criticite: Map<string, number>;
}

function ajouter(m: Map<string, Lien[]>, k: string, l: Lien) {
  const t = m.get(k);
  if (t) t.push(l);
  else m.set(k, [l]);
}

/** Vrai tant que la carte n'a aucun objet (hors objets retirés) : rien n'a encore été lu. */
export function carteVide(carte: Carte): boolean {
  return (carte.objets ?? []).every((o) => o.retire);
}

export function indexer(carte: Carte): IndexCarte {
  const objets = (carte.objets ?? []).filter((o) => !o.retire);
  const liens = (carte.liens ?? []).filter((l) => !l.retire);
  const parId = new Map(objets.map((o) => [o.id, o]));
  const sortants = new Map<string, Lien[]>();
  const entrants = new Map<string, Lien[]>();
  for (const l of liens) {
    ajouter(sortants, l.source, l);
    ajouter(entrants, l.cible, l);
  }
  const zone = new Map<string, string>();
  for (const l of liens) {
    if (l.type === 'situe_dans' && parId.get(l.cible)?.type === 'zone_reseau') zone.set(l.source, l.cible);
  }
  const criticite = new Map<string, number>();
  const propager = (id: string, c: number, vus: Set<string>) => {
    if (vus.has(id)) return;
    vus.add(id);
    if ((criticite.get(id) ?? 0) < c) criticite.set(id, c);
    for (const l of sortants.get(id) ?? []) {
      if (l.type === 'necessite' || l.type === 'depend_de') propager(l.cible, c, vus);
    }
  };
  for (const o of objets) {
    if (o.type !== 'processus') continue;
    const c = Number((o.attributs as { criticite?: number } | undefined)?.criticite ?? 0);
    if (c > 0) propager(o.id, c, new Set());
  }
  return { carte, objets, liens, parId, sortants, entrants, zone, criticite };
}

export function nomDe(ix: IndexCarte, id: string): string {
  return ix.parId.get(id)?.nom ?? idCourt(id);
}

export function nomCourt(o: Objet): string {
  const nc = (o.attributs as { nom_court?: unknown } | undefined)?.nom_court;
  return typeof nc === 'string' && nc ? nc : o.nom;
}

export interface Filtres {
  types: Set<TypeObjet>;
  zone: string | null;
  criticiteMin: number;
  /** Identifiants des objets d'une vue de l'ANSSI, lus dans l'export de l'API (null : pas de vue choisie). */
  idsVue: Set<string> | null;
  recherche: string;
}

export const TYPES_PAR_DEFAUT: TypeObjet[] = [
  'processus',
  'application',
  'machine',
  'sauvegarde',
  'zone_reseau',
  'fournisseur',
];

export function filtrer(ix: IndexCarte, f: Filtres): Objet[] {
  const q = f.recherche.trim().toLocaleLowerCase('fr');
  return ix.objets.filter((o) => {
    if (f.idsVue) {
      if (!f.idsVue.has(o.id)) return false;
    } else if (!f.types.has(o.type)) return false;
    if (f.zone && o.id !== f.zone && ix.zone.get(o.id) !== f.zone) return false;
    if (f.criticiteMin > 0 && (ix.criticite.get(o.id) ?? 0) < f.criticiteMin) return false;
    if (q && !o.nom.toLocaleLowerCase('fr').includes(q) && !o.id.toLowerCase().includes(q)) return false;
    return true;
  });
}

/** Identifiants des objets d'une vue de l'ANSSI, d'après le CSV rendu par l'API (colonne « id »). */
export function idsDeVue(lignes: string[][]): Set<string> {
  const entete = lignes[0] ?? [];
  const col = entete.indexOf('id');
  if (col < 0) return new Set();
  return new Set(lignes.slice(1).map((l) => l[col] ?? '').filter(Boolean));
}

export function contradictionsDe(liste: Contradiction[], id: string): Contradiction[] {
  return liste.filter((c) => c.objet === id || c.sujet.startsWith(`${id}.`) || c.sujet === id);
}

// ---------------------------------------------------------------- disposition du graphe

export interface Noeud {
  id: string;
  x: number;
  y: number;
  l: number;
  h: number;
  couche: Couche;
}

export interface Disposition {
  noeuds: Noeud[];
  liens: Lien[];
  largeur: number;
  hauteur: number;
  couches: { id: Couche; libelle: string; y: number }[];
}

export const NOEUD_L = 132;
export const NOEUD_H = 40;
const PAS_X = 144;
const PAS_Y = 92;
const MARGE_X = 16;
const MARGE_Y = 34;

/** Nombre d'objets par ligne qui tient dans une largeur donnée (pixels). */
export function objetsParLigne(largeur: number): number {
  return Math.max(2, Math.floor((largeur - MARGE_X - 16) / PAS_X));
}

/**
 * Disposition déterministe en couches (une ligne par couche), ordre dans chaque ligne choisi par barycentre des
 * voisins (deux passes) pour limiter les croisements. Même carte, mêmes filtres → même dessin.
 */
export function disposer(ix: IndexCarte, visibles: Objet[], maxParLigne = Infinity): Disposition {
  const ids = new Set(visibles.map((o) => o.id));
  const liens = ix.liens.filter((l) => ids.has(l.source) && ids.has(l.cible));
  const lignes = ORDRE_COUCHES.map((c) => ({
    ...c,
    objets: visibles.filter((o) => COUCHE_DU_TYPE[o.type] === c.id).sort((a, b) => a.id.localeCompare(b.id)),
  })).filter((c) => c.objets.length > 0);

  const voisins = new Map<string, string[]>();
  for (const l of liens) {
    voisins.set(l.source, [...(voisins.get(l.source) ?? []), l.cible]);
    voisins.set(l.cible, [...(voisins.get(l.cible) ?? []), l.source]);
  }
  const rang = new Map<string, number>();
  lignes.forEach((ln) => ln.objets.forEach((o, i) => rang.set(o.id, i - (ln.objets.length - 1) / 2)));
  for (let passe = 0; passe < 2; passe++) {
    for (const ln of lignes) {
      const bary = new Map<string, number>();
      for (const o of ln.objets) {
        const v = (voisins.get(o.id) ?? []).map((x) => rang.get(x)).filter((x): x is number => x !== undefined);
        bary.set(o.id, v.length ? v.reduce((a, b) => a + b, 0) / v.length : (rang.get(o.id) ?? 0));
      }
      ln.objets.sort((a, b) => (bary.get(a.id) ?? 0) - (bary.get(b.id) ?? 0) || a.id.localeCompare(b.id));
      ln.objets.forEach((o, i) => rang.set(o.id, i - (ln.objets.length - 1) / 2));
    }
  }
  // Une couche trop longue pour la largeur disponible se replie sur plusieurs lignes (graphe lisible sans zoom).
  const parLigne = Math.max(2, maxParLigne);
  const max = Math.max(1, Math.min(parLigne, Math.max(...lignes.map((l) => l.objets.length))));
  const largeur = MARGE_X + max * PAS_X + 16;
  const noeuds: Noeud[] = [];
  const couches: Disposition['couches'] = [];
  let rangee = 0;
  for (const ln of lignes) {
    couches.push({ id: ln.id, libelle: ln.libelle, y: MARGE_Y + rangee * PAS_Y });
    for (let debut = 0; debut < ln.objets.length; debut += parLigne) {
      const morceau = ln.objets.slice(debut, debut + parLigne);
      const y = MARGE_Y + rangee * PAS_Y;
      const decalage = MARGE_X + ((max - morceau.length) * PAS_X) / 2;
      morceau.forEach((o, j) => noeuds.push({ id: o.id, x: decalage + j * PAS_X, y, l: NOEUD_L, h: NOEUD_H, couche: ln.id }));
      rangee++;
    }
  }
  return { noeuds, liens, largeur, hauteur: MARGE_Y + rangee * PAS_Y, couches };
}
